import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ClipboardList,
  FileText,
  Loader2,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Trash2,
  Users,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { AdminPageHeader } from "./components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { value: "script_telefonata", label: "Script telefonata", icon: Phone },
  { value: "script_demo", label: "Script demo", icon: Users },
  { value: "obiezione", label: "Obiezioni", icon: FileText },
  { value: "checklist_onboarding", label: "Checklist onboarding", icon: ClipboardList },
];

const EMPTY_PLAYBOOK = {
  servizio: "AFFIDYA",
  tipo: "script_telefonata",
  titolo: "",
  contenuto: "",
  nota_tono: "",
  tag: "",
  ordine: "0",
  attivo: true,
};

function typeMeta(tipo) {
  return TYPES.find((item) => item.value === tipo) || TYPES[0];
}

export default function PlaybookAdminModule({ servizio = "AFFIDYA" }) {
  const token = localStorage.getItem("super_admin_token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PLAYBOOK);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const request = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API}/api/admin/commerciale${path}`, {
      headers,
      ...options,
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(json.detail || json.message || "Operazione non riuscita");
    return json;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const json = await request(`/playbook?servizio=${encodeURIComponent(servizio)}`);
      setItems(json.data || []);
    } catch (error) {
      toast.error(error.message || "Impossibile caricare i playbook");
    } finally {
      setLoading(false);
    }
  }, [request, servizio]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleItems = useMemo(
    () => items
      .filter((item) => filter === "all" || item.tipo === filter)
      .sort((a, b) => (a.ordine || 0) - (b.ordine || 0)),
    [filter, items],
  );

  const openCreate = (tipo = filter === "all" ? "script_telefonata" : filter) => {
    setEditing(null);
    setForm({ ...EMPTY_PLAYBOOK, servizio, tipo });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      servizio,
      tipo: item.tipo || "script_telefonata",
      titolo: item.titolo || "",
      contenuto: item.contenuto || "",
      nota_tono: item.nota_tono || "",
      tag: Array.isArray(item.tag) ? item.tag.join(", ") : (item.tag || ""),
      ordine: String(item.ordine ?? 0),
      attivo: item.attivo !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.titolo.trim() || !form.contenuto.trim()) {
      toast.error("Titolo e contenuto sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        titolo: form.titolo.trim(),
        contenuto: form.contenuto.trim(),
        tag: form.tag.trim(),
        ordine: Number(form.ordine) || 0,
      };
      await request(editing ? `/playbook/${editing.id}` : "/playbook", {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify(payload),
      });
      toast.success(editing ? "Playbook aggiornato" : "Playbook creato");
      setDialogOpen(false);
      await load();
    } catch (error) {
      toast.error(error.message || "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Eliminare "${item.titolo}"?`)) return;
    setDeletingId(item.id);
    try {
      await request(`/playbook/${item.id}`, { method: "DELETE" });
      toast.success("Playbook eliminato");
      await load();
    } catch (error) {
      toast.error(error.message || "Eliminazione non riuscita");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-5">
      <AdminPageHeader
        icon={ClipboardList}
        title={`Playbook ${servizio}`}
        description={`Script, risposte alle obiezioni e checklist per il team ${servizio}.`}
        actions={
        <>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button size="sm" onClick={() => openCreate()}>
            <Plus className="mr-1 h-4 w-4" />
            Nuovo playbook
          </Button>
        </>
        }
      />

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="all">Tutti ({items.length})</TabsTrigger>
          {TYPES.map((type) => (
            <TabsTrigger key={type.value} value={type.value}>{type.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid gap-3 lg:grid-cols-2">
        {visibleItems.map((item) => {
          const meta = typeMeta(item.tipo);
          const Icon = meta.icon;
          const tags = Array.isArray(item.tag)
            ? item.tag
            : String(item.tag || "").split(",").map((tag) => tag.trim()).filter(Boolean);
          return (
            <Card key={item.id} className={item.attivo === false ? "opacity-60" : ""}>
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600"><Icon className="h-5 w-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-slate-900">{item.titolo || "Senza titolo"}</p>
                        <p className="mt-0.5 text-xs text-slate-500">{meta.label} · Ordine {item.ordine ?? 0}</p>
                      </div>
                      <div className="flex shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(item)} aria-label="Modifica">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => remove(item)} disabled={deletingId === item.id} aria-label="Elimina">
                          {deletingId === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap text-sm text-slate-600">{item.contenuto}</p>
                    {item.nota_tono && <details className="mt-3 rounded border bg-amber-50 p-2 text-sm text-amber-900"><summary className="cursor-pointer font-medium">Tono / indicazioni interne</summary><p className="mt-1 whitespace-pre-wrap">{item.nota_tono}</p></details>}
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {item.attivo === false && <Badge variant="secondary">Disattivo</Badge>}
                      {tags.map((tag) => <Badge key={tag} variant="outline">{tag}</Badge>)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {!loading && visibleItems.length === 0 && (
        <Card><CardContent className="p-10 text-center text-sm text-slate-500">Nessun playbook in questa categoria.</CardContent></Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Modifica playbook" : "Nuovo playbook"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select value={form.tipo} onValueChange={(tipo) => setForm((current) => ({ ...current, tipo }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ordine</Label>
                <Input type="number" min="0" value={form.ordine} onChange={(event) => setForm((current) => ({ ...current, ordine: event.target.value }))} />
              </div>
            </div>
            <div className="space-y-2"><Label>Titolo *</Label><Input value={form.titolo} onChange={(event) => setForm((current) => ({ ...current, titolo: event.target.value }))} /></div>
            <div className="space-y-2"><Label>Contenuto *</Label><Textarea rows={10} value={form.contenuto} onChange={(event) => setForm((current) => ({ ...current, contenuto: event.target.value }))} /></div>
            <div className="space-y-2"><Label>Tono / indicazioni interne</Label><Textarea rows={3} value={form.nota_tono} onChange={(event) => setForm((current) => ({ ...current, nota_tono: event.target.value }))} /></div>
            <div className="space-y-2"><Label>Tag (separati da virgola)</Label><Input value={form.tag} onChange={(event) => setForm((current) => ({ ...current, tag: event.target.value }))} /></div>
            <div className="flex items-center gap-3"><Switch checked={form.attivo} onCheckedChange={(attivo) => setForm((current) => ({ ...current, attivo }))} id="playbook-active" /><Label htmlFor="playbook-active">Disponibile per il team</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? "Salva modifiche" : "Crea playbook"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
